import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/confirmation-dialog.css';

function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  icon = 'delete',
  loading = false,
  confirmationText = '',
  onConfirm,
  onCancel,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const cancelButtonRef = useRef(null);
  const [typedConfirmation, setTypedConfirmation] = useState('');

  useEffect(() => {
    if (!open) {
      setTypedConfirmation('');
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => cancelButtonRef.current?.focus(), 0);
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !loading) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [loading, onCancel, open]);

  if (!open) return null;

  const requiresConfirmationText = Boolean(confirmationText);
  const canConfirm = !loading
    && (!requiresConfirmationText || typedConfirmation === confirmationText);

  return createPortal(
    <div
      className="confirmation-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) {
          onCancel();
        }
      }}
    >
      <section
        className="confirmation-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className="confirmation-dialog-icon" aria-hidden="true">
          <span className="material-symbols-outlined">{icon}</span>
        </div>

        <div className="confirmation-dialog-content">
          <h2 id={titleId}>{title}</h2>
          <p id={descriptionId}>{description}</p>

          {requiresConfirmationText && (
            <label className="confirmation-dialog-field">
              <span>
                Type <strong>{confirmationText}</strong> to continue
              </span>
              <input
                type="text"
                value={typedConfirmation}
                onChange={(event) => setTypedConfirmation(event.target.value)}
                autoComplete="off"
                disabled={loading}
              />
            </label>
          )}
        </div>

        <div className="confirmation-dialog-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
            ref={cancelButtonRef}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-danger confirmation-dialog-confirm"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {loading ? 'Deleting...' : confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
